import { messageSchema } from '@chatwoot/prosemirror-schema';
import { Schema } from 'prosemirror-model';

// Extended schema for email channels that supports inline styles
const emailSchema = new Schema({
  nodes: {
    // Copy all nodes from messageSchema but extend them
    ...messageSchema.spec.nodes.toObject(),

    // Enhanced image node to support all HTML attributes and styling
    image: {
      ...messageSchema.spec.nodes.get('image'),
      attrs: {
        src: {},
        alt: { default: null },
        title: { default: null },
        width: { default: null },
        height: { default: null },
        style: { default: null },
        border: { default: null },
        class: { default: null },
      },
      parseDOM: [
        {
          tag: 'img[src]',
          getAttrs: dom => ({
            src: dom.getAttribute('src'),
            alt: dom.getAttribute('alt'),
            title: dom.getAttribute('title'),
            width: dom.getAttribute('width'),
            height: dom.getAttribute('height'),
            style: dom.getAttribute('style'),
            border: dom.getAttribute('border'),
            class: dom.getAttribute('class'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {
          src: node.attrs.src,
        };
        if (node.attrs.alt) attrs.alt = node.attrs.alt;
        if (node.attrs.title) attrs.title = node.attrs.title;
        if (node.attrs.width) attrs.width = node.attrs.width;
        if (node.attrs.height) attrs.height = node.attrs.height;
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.border) attrs.border = node.attrs.border;
        if (node.attrs.class) attrs.class = node.attrs.class;
        return ['img', attrs];
      },
    },

    heading: {
      attrs: { level: { default: 1 } },
      content: 'inline*',
      group: 'block',
      defining: true,
      parseDOM: [
        { tag: 'h1', attrs: { level: 1 } },
        { tag: 'h2', attrs: { level: 2 } },
        { tag: 'h3', attrs: { level: 3 } },
        { tag: 'h4', attrs: { level: 4 } },
        { tag: 'h5', attrs: { level: 5 } },
        { tag: 'h6', attrs: { level: 6 } },
      ],
      toDOM: node => [`h${node.attrs.level}`, 0],
    },

    horizontal_rule: {
      group: 'block',
      parseDOM: [{ tag: 'hr' }],
      toDOM: () => ['hr'],
    },

    div: {
      content: 'block*',
      group: 'block',
      attrs: {
        class: { default: null },
        style: { default: null },
        id: { default: null },
      },
      parseDOM: [
        {
          tag: 'div',
          getAttrs: dom => ({
            class: dom.getAttribute('class'),
            style: dom.getAttribute('style'),
            id: dom.getAttribute('id'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.class) attrs.class = node.attrs.class;
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.id) attrs.id = node.attrs.id;
        return ['div', attrs, 0];
      },
    },

    span: {
      content: 'inline*',
      group: 'block',
      attrs: {
        class: { default: null },
        style: { default: null },
        id: { default: null },
      },
      parseDOM: [
        {
          tag: 'span',
          getAttrs: dom => ({
            class: dom.getAttribute('class'),
            style: dom.getAttribute('style'),
            id: dom.getAttribute('id'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.class) attrs.class = node.attrs.class;
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.id) attrs.id = node.attrs.id;
        return ['span', attrs, 0];
      },
    },

    paragraph: {
      content: 'inline*',
      group: 'block',
      attrs: {
        class: { default: null },
        style: { default: null },
        id: { default: null },
      },
      parseDOM: [
        {
          tag: 'p',
          getAttrs: dom => ({
            class: dom.getAttribute('class'),
            style: dom.getAttribute('style'),
            id: dom.getAttribute('id'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.class) attrs.class = node.attrs.class;
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.id) attrs.id = node.attrs.id;
        return ['p', attrs, 0];
      },
    },

    table: {
      content: '(thead | tbody | tfoot | table_row)+',
      tableRole: 'table',
      isolating: true,
      group: 'block',
      attrs: {
        align: { default: null },
        border: { default: null },
        cellpadding: { default: null },
        cellspacing: { default: null },
        width: { default: null },
        bgcolor: { default: null },
        style: { default: null },
        role: { default: null },
        class: { default: null },
      },
      parseDOM: [
        {
          tag: 'table',
          getAttrs: dom => ({
            align: dom.getAttribute('align'),
            border: dom.getAttribute('border'),
            cellpadding: dom.getAttribute('cellpadding'),
            cellspacing: dom.getAttribute('cellspacing'),
            width: dom.getAttribute('width'),
            bgcolor: dom.getAttribute('bgcolor'),
            style: dom.getAttribute('style'),
            role: dom.getAttribute('role'),
            class: dom.getAttribute('class'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.align) attrs.align = node.attrs.align;
        if (node.attrs.border) attrs.border = node.attrs.border;
        if (node.attrs.cellpadding) attrs.cellpadding = node.attrs.cellpadding;
        if (node.attrs.cellspacing) attrs.cellspacing = node.attrs.cellspacing;
        if (node.attrs.width) attrs.width = node.attrs.width;
        if (node.attrs.bgcolor) attrs.bgcolor = node.attrs.bgcolor;
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.role) attrs.role = node.attrs.role;
        if (node.attrs.class) attrs.class = node.attrs.class;
        return ['table', attrs, 0];
      },
    },

    table_row: {
      content: '(table_cell | table_header)*',
      tableRole: 'row',
      attrs: {
        style: { default: null },
        class: { default: null },
        valign: { default: null },
        align: { default: null },
      },
      parseDOM: [
        {
          tag: 'tr',
          getAttrs: dom => ({
            style: dom.getAttribute('style'),
            class: dom.getAttribute('class'),
            valign: dom.getAttribute('valign'),
            align: dom.getAttribute('align'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.class) attrs.class = node.attrs.class;
        if (node.attrs.valign) attrs.valign = node.attrs.valign;
        if (node.attrs.align) attrs.align = node.attrs.align;
        return ['tr', attrs, 0];
      },
    },

    table_cell: {
      content: 'inline*',
      attrs: {
        colspan: { default: 1 },
        rowspan: { default: 1 },
        colwidth: { default: null },
        style: { default: null },
        class: { default: null },
        align: { default: null },
        valign: { default: null },
        bgcolor: { default: null },
        width: { default: null },
        height: { default: null },
        'padding-left': { default: null },
        'padding-right': { default: null },
        'padding-top': { default: null },
        'padding-bottom': { default: null },
      },
      tableRole: 'cell',
      isolating: true,
      parseDOM: [
        {
          tag: 'td',
          getAttrs: dom => ({
            colspan: parseInt(dom.getAttribute('colspan'), 10) || 1,
            rowspan: parseInt(dom.getAttribute('rowspan'), 10) || 1,
            colwidth: dom.getAttribute('colwidth')
              ? dom
                  .getAttribute('colwidth')
                  .split(',')
                  .map(s => parseInt(s, 10))
              : null,
            style: dom.getAttribute('style'),
            class: dom.getAttribute('class'),
            align: dom.getAttribute('align'),
            valign: dom.getAttribute('valign'),
            bgcolor: dom.getAttribute('bgcolor'),
            width: dom.getAttribute('width'),
            height: dom.getAttribute('height'),
            'padding-left': dom.getAttribute('padding-left'),
            'padding-right': dom.getAttribute('padding-right'),
            'padding-top': dom.getAttribute('padding-top'),
            'padding-bottom': dom.getAttribute('padding-bottom'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.colspan !== 1) attrs.colspan = node.attrs.colspan;
        if (node.attrs.rowspan !== 1) attrs.rowspan = node.attrs.rowspan;
        if (node.attrs.colwidth) {
          attrs.colwidth = node.attrs.colwidth.join(',');
        }
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.class) attrs.class = node.attrs.class;
        if (node.attrs.align) attrs.align = node.attrs.align;
        if (node.attrs.valign) attrs.valign = node.attrs.valign;
        if (node.attrs.bgcolor) attrs.bgcolor = node.attrs.bgcolor;
        if (node.attrs.width) attrs.width = node.attrs.width;
        if (node.attrs.height) attrs.height = node.attrs.height;
        if (node.attrs['padding-left'])
          attrs['padding-left'] = node.attrs['padding-left'];
        if (node.attrs['padding-right'])
          attrs['padding-right'] = node.attrs['padding-right'];
        if (node.attrs['padding-top'])
          attrs['padding-top'] = node.attrs['padding-top'];
        if (node.attrs['padding-bottom'])
          attrs['padding-bottom'] = node.attrs['padding-bottom'];
        return ['td', attrs, 0];
      },
    },

    table_header: {
      content: 'inline*',
      attrs: {
        colspan: { default: 1 },
        rowspan: { default: 1 },
        colwidth: { default: null },
        style: { default: null },
        class: { default: null },
        align: { default: null },
        valign: { default: null },
        bgcolor: { default: null },
        width: { default: null },
        height: { default: null },
      },
      tableRole: 'header_cell',
      isolating: true,
      parseDOM: [
        {
          tag: 'th',
          getAttrs: dom => ({
            colspan: parseInt(dom.getAttribute('colspan'), 10) || 1,
            rowspan: parseInt(dom.getAttribute('rowspan'), 10) || 1,
            colwidth: dom.getAttribute('colwidth')
              ? dom
                  .getAttribute('colwidth')
                  .split(',')
                  .map(s => parseInt(s, 10))
              : null,
            style: dom.getAttribute('style'),
            class: dom.getAttribute('class'),
            align: dom.getAttribute('align'),
            valign: dom.getAttribute('valign'),
            bgcolor: dom.getAttribute('bgcolor'),
            width: dom.getAttribute('width'),
            height: dom.getAttribute('height'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = {};
        if (node.attrs.colspan !== 1) attrs.colspan = node.attrs.colspan;
        if (node.attrs.rowspan !== 1) attrs.rowspan = node.attrs.rowspan;
        if (node.attrs.colwidth) {
          attrs.colwidth = node.attrs.colwidth.join(',');
        }
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.class) attrs.class = node.attrs.class;
        if (node.attrs.align) attrs.align = node.attrs.align;
        if (node.attrs.valign) attrs.valign = node.attrs.valign;
        if (node.attrs.bgcolor) attrs.bgcolor = node.attrs.bgcolor;
        if (node.attrs.width) attrs.width = node.attrs.width;
        if (node.attrs.height) attrs.height = node.attrs.height;
        return ['th', attrs, 0];
      },
    },

    tbody: {
      content: 'table_row*',
      tableRole: 'rowgroup',
      parseDOM: [{ tag: 'tbody' }],
      toDOM: () => ['tbody', 0],
    },

    thead: {
      content: 'table_row*',
      tableRole: 'rowgroup',
      parseDOM: [{ tag: 'thead' }],
      toDOM: () => ['thead', 0],
    },

    tfoot: {
      content: 'table_row*',
      tableRole: 'rowgroup',
      parseDOM: [{ tag: 'tfoot' }],
      toDOM: () => ['tfoot', 0],
    },
  },
  marks: {
    link: {
      attrs: {
        href: {},
        title: { default: null },
        style: { default: null },
        class: { default: null },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: 'a[href]',
          getAttrs: dom => ({
            href: dom.getAttribute('href'),
            title: dom.getAttribute('title'),
            style: dom.getAttribute('style'),
            class: dom.getAttribute('class'),
          }),
        },
      ],
      toDOM: node => {
        const attrs = { href: node.attrs.href };
        if (node.attrs.title) attrs.title = node.attrs.title;
        if (node.attrs.style) attrs.style = node.attrs.style;
        if (node.attrs.class) attrs.class = node.attrs.class;
        return ['a', attrs, 0];
      },
    },
    code: messageSchema.spec.marks.get('code'),
    strike: messageSchema.spec.marks.get('strike'),

    // Enhanced basic formatting that supports both tags and inline styles
    em: {
      parseDOM: [
        { tag: 'i' },
        { tag: 'em' },
        {
          style: 'font-style',
          getAttrs: value => value === 'italic' && null,
        },
      ],
      toDOM: () => ['em', 0],
    },
    strong: {
      parseDOM: [
        { tag: 'strong' },
        { tag: 'b' },
        {
          style: 'font-weight',
          getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null,
        },
      ],
      toDOM: () => ['strong', 0],
    },

    underline: {
      parseDOM: [
        { tag: 'u' },
        {
          style: 'text-decoration',
          getAttrs: value => value === 'underline' && null,
        },
      ],
      toDOM: () => ['u', 0],
    },

    superscript: {
      parseDOM: [{ tag: 'sup' }],
      toDOM() {
        return ['sup'];
      },
    },
    subscript: {
      parseDOM: [{ tag: 'sub' }],
      toDOM() {
        return ['sub'];
      },
    },

    // Extended formatting for email channels with inline styles
    textColor: {
      attrs: { color: {} },
      parseDOM: [
        {
          style: 'color',
          getAttrs: value => ({ color: value }),
        },
      ],
      toDOM: mark => ['span', { style: `color: ${mark.attrs.color}` }, 0],
    },

    backgroundColor: {
      attrs: { backgroundColor: {} },
      parseDOM: [
        {
          style: 'background-color',
          getAttrs: value => ({ backgroundColor: value }),
        },
      ],
      toDOM: mark => [
        'span',
        { style: `background-color: ${mark.attrs.backgroundColor}` },
        0,
      ],
    },

    fontSize: {
      attrs: { size: {} },
      parseDOM: [
        {
          style: 'font-size',
          getAttrs: value => ({ size: value }),
        },
      ],
      toDOM: mark => ['span', { style: `font-size: ${mark.attrs.size}` }, 0],
    },

    fontFamily: {
      attrs: { family: {} },
      parseDOM: [
        {
          style: 'font-family',
          getAttrs: value => ({ family: value }),
        },
      ],
      toDOM: mark => [
        'span',
        { style: `font-family: ${mark.attrs.family}` },
        0,
      ],
    },

    // Table styling marks
    textAlign: {
      attrs: { align: {} },
      parseDOM: [
        {
          style: 'text-align',
          getAttrs: value => ({ align: value }),
        },
      ],
      toDOM: mark => ['span', { style: `text-align: ${mark.attrs.align}` }, 0],
    },

    verticalAlign: {
      attrs: { align: {} },
      parseDOM: [
        {
          style: 'vertical-align',
          getAttrs: value => ({ align: value }),
        },
      ],
      toDOM: mark => [
        'span',
        { style: `vertical-align: ${mark.attrs.align}` },
        0,
      ],
    },
  },
});

export default emailSchema;
